import React, { useCallback, useId, useRef, useState } from 'react';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import { MenuMegaPanel, wantsMegaPanel } from './MenuMegaPanel';
import type { MegaProducts } from './MenuMegaProducts';
import { useCloseOutside } from './useCloseOutside';
import type { MenuLink } from './menuItems';
import './elementMenuMega.css';
import { named, useShopNames } from '../../commerce/shopNames';

// One top-level link that has links under it.
//
// The word and the caret are two separate targets, which is what lets both jobs be done properly: the
// word is an anchor and goes to the parent's own page, the caret is a button and opens the panel. One
// control cannot do both — a link that opens on hover is unreachable from a keyboard, and a button that
// navigates strands the parent page. Splitting them also stops the panel having to repeat the parent as
// its first row, which read as "Shop / Shop".

/** The caret beside a link's words: punctuation-sized, inside a target a thumb can actually hit. */
const CARET = 14;

interface MenuDropdownProps {
  link: MenuLink;
  /** Inside the folded panel a drop-down opens in place; on a desktop bar it opens over the page. */
  folded: boolean;
  /** Whether a mouse passing over opens it. A press always does; this is Shopify's "trigger" setting. */
  hoverOpens: boolean;
  /** Closes the whole menu, which a child link does once a shopper has chosen where they are going. */
  onLeave: () => void;
  /** The product row a mega panel shows at its end edge, when the menu shows one. */
  products: MegaProducts | null;
}

// Draws a plain drop-down's panel: one column of its links.
const ListPanel: React.FC<{ id: string; open: boolean; links: readonly MenuLink[]; onLeave: () => void }> = ({ id, open, links, onLeave }) => (
  <ul id={id} className="xe-menu-panel" data-open={open || undefined}>
    {links.map((child) => (
      <li key={`${child.label}-${child.href ?? ''}`}>
        <a className="xe-menu-sublink" href={child.href} onClick={onLeave} dir="auto">{child.label}</a>
      </li>
    ))}
  </ul>
);

/** Whether a drop-down is open, the item it hangs from, and how it opens and closes. */
type DropdownState = { open: boolean; host: React.RefObject<HTMLLIElement>; show: (next: boolean) => void; hide: () => void };

// Holds one drop-down open or shut; a wide panel is placed under the header's bottom edge as it opens.
function useDropdown(mega: boolean): DropdownState {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLLIElement>(null);
  const hide = useCallback(() => setOpen(false), []);
  // A drop-down opened by a press is not closed by a mouse leaving, because no mouse ever entered it.
  // Escape and a tap elsewhere are the two ways out every shopper already knows.
  useCloseOutside(host, open, hide);
  // A wide panel spans the window, so it needs the one measurement only the page has: where the header ends.
  const show = (next: boolean) => {
    const header = host.current?.closest('header');
    if (next && mega && header) {
      host.current?.style.setProperty('--xe-mega-top', `${Math.round(header.getBoundingClientRect().bottom)}px`);
    }
    setOpen(next);
  };
  return { open, host, show, hide };
}

// Draws one drop-down: the word, the caret that opens it, and the panel of links beneath.
export const MenuDropdown: React.FC<MenuDropdownProps> = ({ link, folded, hoverOpens, onLeave, products }) => {
  const names = useShopNames();
  const mega = wantsMegaPanel(link.children);
  const { open, host, show, hide } = useDropdown(mega);
  const panelId = useId();
  const Caret = ELEMENT_ICONS['chevron-down'] ?? ELEMENT_FALLBACK_ICON;
  // A mouse opens on the way in and closes on the way out; a keyboard and a phone use the caret alone.
  const hover = folded || !hoverOpens ? {} : { onMouseEnter: () => show(true), onMouseLeave: hide };

  return (
    <li
      ref={host}
      className="xe-menu-item"
      data-open={open || undefined}
      {...hover}
      onBlur={(event) => {
        if (!folded && !host.current?.contains(event.relatedTarget as Node)) hide();
      }}
    >
      <span className="xe-menu-head">
        {link.href
          ? <a className="xe-menu-link" href={link.href} onClick={onLeave} dir="auto">{link.label}</a>
          : <span className="xe-menu-link" dir="auto">{link.label}</span>}
        <button
          type="button"
          className="xe-menu-caret-button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={named(open ? names.hideMenu : names.showMenu, { label: link.label })}
          onClick={() => show(!open)}
        >
          <Caret size={CARET} strokeWidth={2} aria-hidden="true" className="xe-menu-caret" />
        </button>
      </span>
      {mega
        ? <MenuMegaPanel id={panelId} open={open} links={link.children} products={folded ? null : products} onLeave={onLeave} />
        : <ListPanel id={panelId} open={open} links={link.children} onLeave={onLeave} />}
    </li>
  );
};
